import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useGestor } from '@/contexts/GestorContext'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ArrowDownRight, ArrowUpRight, Calendar } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface Movimentacao {
  id: string
  created_at: string
  valor: number
  status: string
  tipo_despesa_origem_nome?: string
  tipo_despesa_destino_nome?: string
  subtipo_origem?: string
  subtipo_destino?: string
  is_entrada: boolean
}

export default function GestorMovimentacoesPage() {
  const { areaSelecionada, anoSelecionado } = useGestor()
  const [data, setData] = useState<Movimentacao[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaSelecionada) return
    
    setLoading(true)
    try {
      // Buscar o ID do centro de custo da área selecionada
      const { data: ccData } = await supabase
        .from('solis_centros_custo')
        .select('id, pipefy_database_id')
        .eq('nome', areaSelecionada)
        .limit(1)
        .single()

      if (!ccData) {
        setData([])
        setLoading(false)
        return
      }

      // Buscar movimentações onde a área é origem ou destino
      // Simplificando a query para pegar as movimentações recentes
      const { data: movData, error } = await supabase
        .from('solis_movimentacoes_despesa')
        .select(`
          id,
          created_at,
          valor,
          status,
          centro_custo_origem_id,
          centro_custo_destino_id,
          subtipo_origem,
          subtipo_destino
        `)
        .eq('ano', parseInt(anoSelecionado))
        .or(`centro_custo_origem_id.eq.${ccData.pipefy_database_id},centro_custo_destino_id.eq.${ccData.pipefy_database_id}`)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error

      const formattedData = (movData || []).map(mov => ({
        ...mov,
        is_entrada: mov.centro_custo_destino_id === ccData.pipefy_database_id
      }))

      setData(formattedData)
    } catch (error) {
      console.error('Erro ao buscar movimentações:', error)
      toast.error('Erro ao carregar histórico de movimentações.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaSelecionada])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Card className="border-none shadow-sm bg-white">
        <CardHeader className="pb-4 border-b border-slate-100">
          <CardTitle className="text-base font-semibold text-slate-800">Últimas Movimentações</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/50">
                <TableRow className="hover:bg-transparent border-slate-100">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 pl-6">Data</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4">Tipo</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4">Origem</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4">Destino</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 text-right pr-6">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center text-slate-500">
                      Nenhuma movimentação encontrada para este ano.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow key={item.id} className="border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <TableCell className="py-4 pl-6 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {format(new Date(item.created_at), "dd 'de' MMM, yyyy", { locale: ptBR })}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        {item.is_entrada ? (
                          <Badge variant="outline" className="bg-orange-50 text-[#131E29] border-orange-200 gap-1">
                            <ArrowDownRight className="h-3 w-3" /> Entrada
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 gap-1">
                            <ArrowUpRight className="h-3 w-3" /> Saída
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="py-4 text-sm text-slate-700 font-medium">
                        {item.subtipo_origem || 'N/A'}
                      </TableCell>
                      <TableCell className="py-4 text-sm text-slate-700 font-medium">
                        {item.subtipo_destino || 'N/A'}
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 hover:bg-slate-100">
                          {item.status}
                        </Badge>
                      </TableCell>
                      <TableCell className={`py-4 pr-6 text-right font-semibold text-sm ${item.is_entrada ? 'text-orange-600' : 'text-slate-700'}`}>
                        {item.is_entrada ? '+' : '-'}{formatCurrency(item.valor)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
